import type { IVariantType } from '~/modules/campaign/types';
import type { ERowReaderMode } from '~/core/gemxql/types';
import type { IAnalyticMetric } from '~/types/metric';

import {
  createNumericRowReader,
  createRawRowReader,
  parseBreakdownItems,
} from '~/core/gemxql/helpers';
import { EAnalyticColumnKey } from '~/core/gemxql/types';
import { EMetricKey } from '~/types/metric';

/** GemxQL returns rate metrics already scaled to 0–100, not as a 0–1 ratio. */
const PERCENT_SCALE = 100;

export const toBreakdownStat = (raw: unknown) =>
  (parseBreakdownItems(raw) ?? []).map((item) => ({ type: item.type, totalCount: item.total }));

export interface IBuildAnalyticMetricParams {
  row: Record<string, unknown>;
  readerMode: ERowReaderMode;
  date: string;
  variantKey: IVariantType;
  /** First GROUP BY dimension, required by `ERowReaderMode.GROUP_TOTALS` to resolve its columns. */
  groupDimension?: string;
}

export const buildAnalyticMetric = ({
  row,
  readerMode,
  date,
  variantKey,
  groupDimension,
}: IBuildAnalyticMetricParams): IAnalyticMetric => {
  const read = createNumericRowReader(row, readerMode, groupDimension);
  const readRaw = createRawRowReader(row, readerMode, groupDimension);

  const sessions = read(EMetricKey.SESSION);
  const pageviews = read(EMetricKey.PAGE_VIEWS);
  const bounceRate = read(EMetricKey.BOUNCE_RATE);
  const ctr = read(EMetricKey.CTR);
  const avgTimeOnPage = read(EMetricKey.AVG_TIME_ON_PAGE);
  const reachedCheckout = read(EMetricKey.REACHED_CHECKOUT);
  const completedCheckout = read(EMetricKey.COMPLETE_CHECKOUT);

  const totalClicked = (ctr / PERCENT_SCALE) * pageviews;

  return {
    date,
    totalSessions: sessions,
    totalVisitors: read(EMetricKey.VISITORS),
    totalPageViewed: pageviews,
    totalOrders: read(EMetricKey.ORDERS),
    totalRevenue: read(EMetricKey.REVENUE),
    totalAddedToCart: read(EMetricKey.ADDED_TO_CART),
    totalSessionsWithCartAdditions: read(EMetricKey.CART_ADDITION),
    totalSessionsThatReachedCheckout: reachedCheckout,
    totalReachedCheckout: reachedCheckout,
    totalSessionsThatCompletedCheckout: completedCheckout,
    totalSessionsThatReachedAndCompletedCheckout: completedCheckout,
    totalClicked,
    totalClickedHasHref: totalClicked,
    totalSessionsWithoutAction: (bounceRate / PERCENT_SCALE) * sessions,
    totalTimeOnPage: avgTimeOnPage * pageviews,
    totalPageViewedHaveTimeOnPage: pageviews,
    channels: toBreakdownStat(readRaw(EAnalyticColumnKey.TRAFFIC_SOURCE_ITEMS)),
    devices: toBreakdownStat(readRaw(EAnalyticColumnKey.DEVICE_ITEMS)),
    visitors: toBreakdownStat(readRaw(EAnalyticColumnKey.VISITOR_ITEMS)),
    variantKey,
  };
};
